/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { CONTENT_MAX_WIDTH_AUTH } from '../../shared/layout/breakpoints';
import { logout } from '../../auth/application/authSession';
import { acceptEmployeeInvitation, rejectEmployeeInvitation } from '../../team/application/employeeInvitations';
import type { PendingEmployeeInvitation } from '../../auth/domain/accountSetup';

type Props = {
  invitation: PendingEmployeeInvitation;
  /** Se llama tras aceptar exitosamente para refrescar la sesión. */
  onAccepted?: () => void | Promise<void>;
  /** Se llama tras rechazar para refrescar el estado (sacar el banner). */
  onRejected?: () => void | Promise<void>;
};

/**
 * Pantalla para usuarios con invitación de empleado pendiente.
 * - Si NO son dueños: botón para aceptar o rechazar la invitación.
 * - Si son dueños: aviso de que no pueden aceptar + botón para rechazar
 *   (y así sacar la invitación de su cuenta).
 */
export const PendingInvitationScreen = ({ invitation, onAccepted, onRejected }: Props) => {
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [rejected, setRejected] = useState(false);

  const handleAccept = useCallback(async () => {
    setError(null);
    setAccepting(true);
    try {
      const result = await acceptEmployeeInvitation(invitation.token);
      if (result.ok) {
        setSuccess(true);
        setAccepting(false);
        await onAccepted?.();
      } else {
        setError(result.message);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No pudimos aceptar la invitación.';
      setError(msg);
    } finally {
      setAccepting(false);
    }
  }, [invitation.token, onAccepted]);

  const handleReject = useCallback(async () => {
    setError(null);
    setRejecting(true);
    try {
      const result = await rejectEmployeeInvitation(invitation.token);
      if (result.ok) {
        setRejected(true);
        setRejecting(false);
        await onRejected?.();
      } else {
        setError(result.message);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No pudimos rechazar la invitación.';
      setError(msg);
    } finally {
      setRejecting(false);
    }
  }, [invitation.token, onRejected]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, []);

  if (rejected) {
    return (
      <View style={styles.root}>
        <ScrollView contentContainerStyle={styles.scrollContent} alwaysBounceVertical={false}>
          <View style={styles.card}>
            <View style={styles.iconWrap}>
              <Ionicons name="close-circle" size={56} color={ticketColors.ink3} />
            </View>
            <Text style={styles.title}>Invitación rechazada</Text>
            <Text style={styles.subtitle}>
              {invitation.organizationName
                ? `Rechazaste la invitación a “${invitation.organizationName}”.`
                : 'Rechazaste la invitación.'}
            </Text>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (invitation.isOwner) {
    return (
      <View style={styles.root}>
        <ScrollView contentContainerStyle={styles.scrollContent} alwaysBounceVertical={false}>
          <View style={styles.card}>
            <View style={styles.iconWrap}>
              <Ionicons name="information-circle-outline" size={48} color={ticketColors.ink3} />
            </View>
            <Text style={styles.title}>No podés aceptar esta invitación</Text>
            <Text style={styles.subtitle}>
              Esta cuenta ya es dueña de un negocio en Vuelto. Para entrar como empleado
              {invitation.organizationName ? ` en “${invitation.organizationName}”` : ''},
              pedile al dueño que te invite con otro correo.
            </Text>
            {error ? (
              <View style={styles.errorBanner}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
            <View style={styles.actions}>
              <TouchableOpacity
                style={styles.btnPrimary}
                onPress={() => void handleReject()}
                disabled={rejecting}
                accessibilityRole="button"
                accessibilityLabel="Rechazar invitación"
              >
                {rejecting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.btnPrimaryText}>Rechazar invitación</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.btnSecondary}
                onPress={() => void handleLogout()}
                disabled={rejecting}
                accessibilityRole="button"
                accessibilityLabel="Cerrar sesión"
              >
                <Text style={styles.btnSecondaryText}>Cerrar sesión</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (success) {
    return (
      <View style={styles.root}>
        <ScrollView contentContainerStyle={styles.scrollContent} alwaysBounceVertical={false}>
          <View style={styles.card}>
            <View style={styles.iconWrap}>
              <Ionicons name="checkmark-circle" size={56} color={ticketColors.ok} />
            </View>
            <Text style={styles.title}>¡Listo! Ya sos parte del equipo</Text>
            <Text style={styles.subtitle}>
              {invitation.organizationName
                ? `Te uniste a “${invitation.organizationName}”.`
                : 'Tu invitación fue aceptada.'}
              {'\n\n'}Abriendo tu sesión de empleado…
            </Text>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView contentContainerStyle={styles.scrollContent} alwaysBounceVertical={false}>
        <View style={styles.card}>
          <View style={styles.iconWrap}>
            <Ionicons name="mail-open-outline" size={48} color={ticketColors.gold} />
          </View>
          <Text style={styles.title}>Tenés una invitación pendiente</Text>
          <Text style={styles.subtitle}>
            {invitation.organizationName
              ? `Te invitaron a unirte a “${invitation.organizationName}”.`
              : 'Te invitaron a unirte al equipo de un negocio.'}
            {'\n\n'}Aceptá la invitación para empezar a usar la app como empleado.
          </Text>
          {error ? (
            <View style={styles.errorBanner}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.btnPrimary, accepting && styles.btnDisabled]}
              onPress={() => void handleAccept()}
              disabled={accepting || rejecting}
              accessibilityRole="button"
              accessibilityLabel="Aceptar invitación"
            >
              {accepting ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.btnPrimaryText}>Aceptar invitación</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.btnSecondary}
              onPress={() => void handleReject()}
              disabled={accepting || rejecting}
              accessibilityRole="button"
              accessibilityLabel="Rechazar invitación"
            >
              {rejecting ? (
                <ActivityIndicator color={ticketColors.ink2} />
              ) : (
                <Text style={styles.btnSecondaryText}>Rechazar</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.btnSecondary}
              onPress={() => void handleLogout()}
              disabled={accepting || rejecting}
              accessibilityRole="button"
              accessibilityLabel="Cerrar sesión"
            >
              <Text style={styles.btnSecondaryText}>Cerrar sesión</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH_AUTH,
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    padding: spacing.xl,
    alignItems: 'center',
    gap: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  iconWrap: {
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  errorBanner: {
    width: '100%',
    backgroundColor: ticketColors.paper2,
    borderRadius: 8,
    padding: spacing.sm + spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  errorText: {
    color: ticketColors.bad,
    fontSize: 14,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  actions: {
    width: '100%',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  btnPrimary: {
    backgroundColor: ticketColors.gold,
    paddingVertical: spacing.md,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  btnPrimaryText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 16,
    fontFamily: ticketFonts.sans,
  },
  btnSecondary: {
    paddingVertical: spacing.md,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  btnSecondaryText: {
    color: ticketColors.ink2,
    fontWeight: '600',
    fontSize: 15,
    fontFamily: ticketFonts.sans,
  },
  btnDisabled: {
    opacity: 0.6,
  },
});
